import React, { createContext, useState, useEffect, useContext } from 'react';

// Create context
const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // On component mount, check for existing user session
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        // Check if there's a stored auth token in localStorage
        const token = localStorage.getItem('authToken');
        
        if (token) {
          // Here you would typically validate the token with your backend
          // For this example, we'll just assume the token is valid and set a mock user
          const userData = { id: '1', name: 'Demo User', email: 'user@example.com' };
          setUser(userData);
        }
      } catch (err) {
        console.error('Error checking user session:', err);
        setError('Failed to authenticate user');
      } finally {
        setLoading(false);
      }
    };

    checkUserSession();
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    try {
      // This would be replaced with an actual API call to your login endpoint
      // Mock login logic
      if (email === 'user@example.com' && password === 'password123') {
        const userData = { id: '1', name: 'Demo User', email };
        const token = 'mock-auth-token';
        
        // Store auth token in localStorage
        localStorage.setItem('authToken', token);
        
        setUser(userData);
        setError(null);
        return { success: true };
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (name, email, password) => {
    setLoading(true);
    try {
      // This would be replaced with an actual API call to your register endpoint
      // Mock register logic
      if (email && password && name) {
        const userData = { id: '1', name, email };
        const token = 'mock-auth-token';
        
        // Store auth token in localStorage
        localStorage.setItem('authToken', token);
        
        setUser(userData);
        setError(null);
        return { success: true };
      } else {
        throw new Error('Please provide all required fields');
      }
    } catch (err) {
      setError(err.message || 'Registration failed');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // Remove auth token from localStorage
    localStorage.removeItem('authToken');
    setUser(null);
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
